import { cloneDeep, get, merge } from 'lodash'

import { DateFields, Services } from '@sigma-frontend-libs/commons'
import Macro from '@sigma-legacy-libs/cache'
import { isUUID } from '@sigmasms/commons'
import { OrderDirections } from '@sigmasms/shared-types'

import { globalErrorHandler, labelModes, macroCacheOptions, projectName, proxy, request } from '@/utils'

import render from './render'

const Cache = new Macro(macroCacheOptions)
const cachedFind = Cache.wrapWithCache(async (key, query) => {
  return await request.find(Services.sendernames, undefined, query)
})

const hasItems = items => {
  return Array.isArray(items) && items.length > 0
}

const defaultFilter = {
  type: undefined,
  name: undefined,
  isActive: true,
  $offset: 0,
  $limit: 25,
  $order: [ [ DateFields.createdAt, OrderDirections.DESC ] ],
  $search: undefined,
  $scope: [ 'usable' ]
}

export default {
  name: 'SelectorSendername',

  mixins: [ proxy() ],

  props: {
    type: String,
    label: String,

    itemValue: {
      type: String,
      default: 'name'
    },

    required: {
      type: Boolean,
      default: true
    },
    setRequiredOnMounted: {
      type: Boolean,
      default: true
    },

    loading: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    combobox: Boolean,
    dense: Boolean,
    multiple: Boolean,
    clearable: Boolean,

    labelMode: {
      type: String,
      default: labelModes.hidden,
      validator(value) {
        return Object.values(labelModes).includes(value)
      }
    },

    itemsDisabled: {
      type: Array,
      default: () => {
        return []
      }
    },
    errors: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },

  data() {
    return {
      checkLoading: false,
      nameFound: false,
      hasSendernames: undefined,
      itemsPrepend: [],
      fetchedItems: [],
      lastSearch: undefined,
      selectedId: undefined
    }
  },

  computed: {
    /**
     * Определяет режим combobox (разрешен ли произвольный ввод имени отправителя).
     *
     * Приоритеты:
     * 1. Явно переданный prop combobox.
     * 2. Если sendings.sendernamesCheck отсутствует, ввод считаем разрешенным.
     * 3. Если массив задан, ввод разрешен для типов, которых нет в списке проверки.
     *
     * @returns {boolean}
     */
    $_combobox() {
      if (this.combobox) {
        return true
      }

      const sendernamesCheck = get(this.globalSettings, 'sendings.sendernamesCheck')
      if (!Array.isArray(sendernamesCheck)) {
        return true
      }

      return !sendernamesCheck.includes(this.type)
    },

    /**
     * Признак отсутствия имен отправителя для текущего типа.
     *
     * true только когда загрузка списка без поискового запроса вернула пустой
     * результат и свободный ввод запрещен. В combobox-режиме всегда false,
     * потому что пользователь может ввести имя вручную. Считается реактивно,
     * поэтому смена типа или настроек сразу обновляет состояние в родителе.
     *
     * @returns {boolean}
     */
    noSendernames() {
      if (this.$_combobox) {
        return false
      }

      return this.hasSendernames === false
    },

    $_loading() {
      return this.loading || this.checkLoading
    },

    /**
     * Формирует базовый query для поиска sendername по выбранному типу отправки.
     *
     * Используется g-select при каждом запросе списка и в логике синхронизации.
     * В query всегда подмешивается текущий this.type.
     *
     * @returns {object}
     */
    query() {
      return merge(cloneDeep(defaultFilter), { type: this.type })
    },

    /**
     * Разрешает дополнительную проверку sendername через hydrateSender.
     *
     * Проверка запускается только когда:
     * - в proxy есть значение;
     * - proxy не UUID;
     * - нет локальной загрузки;
     * - имя еще не было подтверждено ранее.
     *
     * Работает и в combobox-режиме: гидрация подтягивает id и type,
     * без которых selection не может корректно отобразить выбранное имя.
     *
     * @returns {boolean}
     */
    canCheck() {
      return !!this.proxy &&
        !isUUID(this.proxy) &&
        !this.$_loading &&
        !this.nameFound
    }
  },

  watch: {
    type() {
      this.nameFound = false
      this.hasSendernames = undefined
      this.itemsPrepend = []
      this.fetchedItems = []
      this.lastSearch = undefined
      this.selectedId = undefined
    },

    noSendernames(value) {
      this.$emit('sendernames-state', !value)
    },

    proxy(value) {
      if (!value) {
        this.itemsPrepend = []
        this.selectedId = undefined

        return
      }

      // Значение могло измениться извне (например сброс формы вернул тестового
      // отправителя) без перезапроса списка, поэтому пересчитываем подсветку
      // под новый proxy - иначе selectedId остается на прежнем имени и оно
      // выглядит выбранным и заблокированным.
      this.syncSelectedId()
    }
  },

  methods: {
    /**
     * Обработчик события input от g-select.
     * @param {string|undefined} value - выбранное значение по полю itemValue
     */
    handleInput(value) {
      this.proxy = value
    },

    /**
     * Обработчик клика по элементу списка из слота listItem.
     * Запоминает id конкретного выбранного элемента до вызова toggleByValue,
     * чтобы подсветка в списке была уникальной по id, а не по имени.
     * @param {{ id: string, [key: string]: any }} item - подготовленный элемент списка от g-select
     * @param {function(string): void} toggleByValue - метод g-select для выбора/снятия элемента по значению
     */
    handleItemClick(item, toggleByValue) {
      this.selectedId = item.id
      toggleByValue(get(item, this.itemValue, item))
    },

    /**
     * Обработчик события search от g-select.
     * Сохраняет последний поисковый запрос, чтобы в handleFetched отличить
     * первичную загрузку (lastSearch пустой) от поиска пользователем.
     * @param {string} value - строка поискового запроса
     */
    handleSearch(value) {
      this.lastSearch = value
    },

    /**
     * Проверяет, что загруженные элементы соответствуют текущему типу.
     * Защита от устаревших ответов: при смене типа ответ предыдущего
     * запроса может прийти позже и не должен влиять на состояние
     * (автовыбор первого элемента, hasSendernames, синхронизацию).
     * Пустой список отличить не от чего, поэтому он считается релевантным.
     * @param {Array<object>|undefined} items - загруженные элементы от g-select (fetched)
     * @returns {boolean}
     */
    isRelevantFetch(items) {
      if (!this.type || !hasItems(items)) {
        return true
      }

      return items.every(item => {
        return item?.type === this.type
      })
    },

    /**
     * Ищет в уже загруженном списке элемент, чьё поле itemValue совпадает с текущим proxy.
     * Используется для восстановления selectedId без лишнего сетевого запроса.
     * @param {Array<object>|undefined} items - массив элементов от g-select (fetched)
     * @returns {object|undefined} найденный элемент или undefined
     */
    findMatchingItem(items) {
      if (!hasItems(items)) {
        return undefined
      }

      return items.find(item => {
        return item?.[this.itemValue] === this.proxy
      })
    },

    /**
     * Пересчитывает selectedId под текущий proxy по уже известным элементам
     * (подмешанные гидрацией + последний загруженный список). Если совпадения
     * нет (например свободно введенное имя) - подсветка снимается.
     */
    syncSelectedId() {
      const knownItems = this.itemsPrepend.concat(this.fetchedItems)
      const matchingItem = this.findMatchingItem(knownItems)
      this.selectedId = matchingItem?.id
    },

    /**
     * Делает сетевой запрос для гидрации proxy по имени отправителя.
     * Нужен когда proxy пришёл извне (сохранённая форма) и не совпал
     * ни с одним элементом в загруженном списке.
     * Найденный элемент кладется в itemsPrepend и через itemsPrepend
     * попадает в g-select, чтобы selection получил полные данные (id, type).
     * При пустом ответе значение не трогаем: это может быть тестовый
     * отправитель из testPayload или произвольное имя - валидность
     * проверяет сервер при отправке.
     * @returns {Promise<void>}
     */
    async hydrateSender() {
      const query = merge(cloneDeep(defaultFilter), {
        name: this.proxy,
        type: this.type
      })
      const key = [ projectName, Services.sendernames, 'find', JSON.stringify(query) ].filter(Boolean).join(':')
      const data = await cachedFind(key, query)
      const item = get(data, 'data[0]')
      if (item) {
        this.proxy = get(item, this.itemValue)
        this.selectedId = item.id
        this.itemsPrepend = [ item ]
        this.nameFound = true
      }
    },

    /**
     * Проверяет и синхронизирует выбранного отправителя после загрузки списка.
     * Сначала ищет совпадение локально через findMatchingItem — если найдено,
     * восстанавливает selectedId без запроса и очищает itemsPrepend, чтобы
     * элемент не дублировался в списке. Иначе, если canCheck разрешает,
     * запускает hydrateSender для разрешения имени через API.
     * @param {Array<object>} items - загруженные элементы от g-select
     * @returns {Promise<void>}
     */
    async checkSender(items) {
      const matchingItem = this.findMatchingItem(items)
      if (matchingItem) {
        this.selectedId = matchingItem.id
        this.itemsPrepend = []

        return
      }
      if (!this.canCheck) {
        return
      }
      try {
        this.checkLoading = true
        await this.hydrateSender()
      } catch (error) {
        globalErrorHandler(error)
      } finally {
        this.checkLoading = false
      }
    },

    /**
     * Обработчик события fetched от g-select, вызывается после каждой загрузки списка.
     * Ответы, не соответствующие текущему типу (isRelevantFetch), игнорируются.
     * Для загрузки без поискового запроса запоминает в hasSendernames, есть ли
     * у пользователя имена текущего типа - от этого реактивно зависит noSendernames.
     * Если список не пуст: при пустом proxy и активных флагах required + setRequiredOnMounted
     * автоматически выбирает первый элемент; затем вызывает checkSender для синхронизации.
     * @param {Array<object>} items - загруженные элементы (передаются до записи в g-select)
     */
    handleFetched(items) {
      if (!this.isRelevantFetch(items)) {
        return
      }
      this.fetchedItems = []
      if (Array.isArray(items)) {
        this.fetchedItems = items
      }
      if (!this.lastSearch) {
        this.hasSendernames = hasItems(items)
      }
      if (hasItems(items)) {
        if (this.required && this.setRequiredOnMounted && !this.proxy) {
          this.proxy = items[0]?.[this.itemValue]
          this.selectedId = items[0]?.id
        }
        this.checkSender(items)
      }
    }
  },

  render
}
